import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchSchoolActive,
  fetchSchoolHistory,
  fetchDistrictActive,
  disableSchool,
  updateSchool,
  revokeSchool,
} from "../api/accessControl.api";
import {
  mapFlagToRestriction,
  mapFlagsToAuditHistory,
} from "../utils/accessControl.utils";
import { QUERY_KEYS } from "../constants";

export function useSchoolAccess() {
  const queryClient = useQueryClient();

  const schoolActiveQuery = useQuery({
    queryKey: QUERY_KEYS.schoolActive,
    queryFn: fetchSchoolActive,
    select: (flag) => (flag ? mapFlagToRestriction(flag) : null),
  });

  const schoolHistoryQuery = useQuery({
    queryKey: QUERY_KEYS.schoolHistory,
    queryFn: fetchSchoolHistory,
    select: mapFlagsToAuditHistory,
  });

  const districtActiveQuery = useQuery({
    queryKey: QUERY_KEYS.districtActive,
    queryFn: fetchDistrictActive,
    select: (flag) => {
      if (!flag) return null;
      const restriction = mapFlagToRestriction(flag);
      const hasStarted =
        !restriction.start || new Date(restriction.start) <= new Date();
      return hasStarted ? restriction : null;
    },
  });

  const createMutation = useMutation({
    mutationFn: (dto) => disableSchool(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.schoolActive });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.schoolHistory });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (dto) => updateSchool(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.schoolActive });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.schoolHistory });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (dto) => revokeSchool(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.schoolActive });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.schoolHistory });
    },
  });

  const extractError = (err, fallback) => {
    if (!err) return null;
    const data = err?.response?.data;
    if (data?.code === "ACTIVE_DISABLE_EXISTS") {
      return "A school pause is already active. End it before creating a new one.";
    }
    const msg = data?.message;
    if (Array.isArray(msg)) return msg.join(", ");
    if (typeof msg === "string") return msg;
    return fallback;
  };

  return {
    activeRestriction: schoolActiveQuery.data ?? null,
    districtRestriction: districtActiveQuery.data ?? null,
    auditHistory: schoolHistoryQuery.data ?? [],
    isLoading:
      schoolActiveQuery.isLoading ||
      schoolHistoryQuery.isLoading ||
      districtActiveQuery.isLoading,
    isError: schoolActiveQuery.isError || schoolHistoryQuery.isError,

    create: createMutation.mutate,
    update: updateMutation.mutate,
    revoke: revokeMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isRevoking: revokeMutation.isPending,

    createError: extractError(
      createMutation.error,
      "Failed to schedule school pause. Please try again.",
    ),
    updateError: extractError(
      updateMutation.error,
      "Failed to update school pause. Please try again.",
    ),
    revokeError: extractError(
      revokeMutation.error,
      "Failed to end school pause. Please try again.",
    ),
  };
}
